const DROPDOWN_OPEN_CLASSNAME = `dropdown--open`;
const MODES = {
  MULTI: `multi`,
  SINGLE: `single`,
};

class DropdownModel {
  constructor(options, defaultValue, initialSelected = [], mode = MODES.MULTI, allValue = `ALL`) {
    this.options = new Map(options);
    this.mode = mode;
    this.selectedValues = new Map();
    initialSelected.forEach((selected) =>
      this.selectedValues.set(selected, this.options.get(selected)),
    );
    this.allValue = allValue;
    this.defaultValue = defaultValue;
    this.toggleValue = this.toggleValue.bind(this);
  }

  toggleValue(value) {
    if (this.mode !== MODES.MULTI) {
      this.selectedValues = new Map([[value, this.options.get(value)]]);
      return;
    }

    if (value === this.allValue) {
      this.selectedValues.clear();
      this.selectedValues.set(this.allValue, this.options.get(this.allValue));
      return;
    }

    if (this.selectedValues.get(value)) {
      this.selectedValues.delete(value);
    } else {
      this.selectedValues.set(value, this.options.get(value));
    }
    this.selectedValues.delete(this.allValue);
  }

  getSelected() {
    return Array.from(this.selectedValues.keys());
  }

  setSelected(values) {
    this.selectedValues = new Map();
    values.forEach((value) => this.selectedValues.set(value, this.options.get(value)));
  }

  clear() {
    this.selectedValues.clear();
    if (this.defaultValue) {
      this.selectedValues.set(this.defaultValue, this.options.get(this.defaultValue));
    }
  }
}

class DropdownView {
  constructor(rootElement, options, name, label, mode = MODES.MULTI) {
    this.root = rootElement;
    this.options = new Map(options);
    this.name = name;
    this.label = label;
    this.mode = mode;
    this.onToggleOption = null;
    this.render();
  }

  render() {
    this.root.innerHTML = `
      <div class="dropdown dropdown--${this.mode}">
        <span class="dropdown__label">${this.label}</span>
        <div class="dropdown__selected--wrapper">
        <div class="dropdown__selected">Choose options</div>
        <span class="dropdown__icon"></span>
        </div>
        <div class="dropdown__options">
  ${[...this.options.entries()]
    .map(
      ([key, value]) => `
            <label class="dropdown__option">
            <input type="checkbox" value="${key}" class="dropdown__checkbox">
              ${this.mode === MODES.MULTI ? `<span class="dropdown__checkmark"></span>` : ``}
              ${value}
            </label>`,
    )
    .join(``)}
        </div>
      </div>
      <div class="dropdown__hidden-input">
        <input type="hidden" name="${this.name}" value="" id="${this.name}-input">
      </div>
  `;

    this.selectedElement = this.root.querySelector(`.dropdown__selected`);
    this.dropdownElement = this.root.querySelector(`.dropdown`);
    this.hiddenInputElement = this.root.querySelector(`#${this.name}-input`);
    this.dropdownOptions = [...this.root.querySelectorAll(`.dropdown__option`)].map(
      (optionElement) => [optionElement, optionElement.querySelector(`.dropdown__checkbox`)],
    );

    this.selectedElement.addEventListener(`click`, () => {
      this.dropdownElement.classList.toggle(DROPDOWN_OPEN_CLASSNAME);
    });

    this.dropdownOptions.forEach(([_, checkboxElement]) => {
      checkboxElement.addEventListener(`change`, () => {
        if (this.onToggleOption) {
          this.onToggleOption(checkboxElement.value);
        }

        if (this.mode !== MODES.MULTI) {
          this.dropdownElement.classList.toggle(DROPDOWN_OPEN_CLASSNAME);
        }
      });
    });

    document.addEventListener(`click`, (e) => {
      if (!this.root.contains(e.target)) {
        this.dropdownElement.classList.remove(DROPDOWN_OPEN_CLASSNAME);
      }
    });
  }

  updateSelected(values) {
    this.selectedElement.textContent =
      values.length > 0
        ? values
            .map((value) => this.options.get(value))
            .filter(Boolean)
            .join(`, `)
        : `Choose option`;

    this.dropdownOptions.forEach(([optionElement, checkboxElement]) => {
      const checked = values.includes(checkboxElement.value);
      checkboxElement.checked = checked;
      checked
        ? optionElement.classList.add(`selected`)
        : optionElement.classList.remove(`selected`);
    });

    this.hiddenInputElement.value = values.join(`,`);
  }
}

class DropdownController {
  constructor(model, view) {
    this.model = model;
    this.view = view;

    this.view.onToggleOption = this.handleToggleOption.bind(this);
    this.view.updateSelected(this.model.getSelected());
  }

  handleToggleOption(value) {
    this.model.toggleValue(value);
    const selected = this.model.getSelected();
    this.view.updateSelected(selected);
  }

  clear() {
    this.model.clear();
    const selected = this.model.getSelected();
    this.view.updateSelected(selected);
  }

  updateSelected(values) {
    this.model.setSelected(values);
    const selected = this.model.getSelected();
    this.view.updateSelected(selected);
  }
}

export { DropdownController, DropdownModel, DropdownView, MODES };
